import { CONFIRMATION_STATE_WITH_FEE_LIST } from '@/base/const/confirmation.const'
import { PAYWAY_GROUPS_TYPES } from '@/base/const/payway.const'
import { REPORT_LEVEL, reportMessage } from '@/base/utils/errorReporting'

const formatFee = (value) => Number(value).toFixed(2).replace('.', ',')

/**
 * Transakcje, dla których brak prowizji w danych potwierdzenia został już zgłoszony.
 *
 * `getPaymentFeeAsSingleValue` jest wołane wprost z szablonu (kilka razy na render), więc bez tego
 * jedna odpowiedź API dawałaby serię identycznych zdarzeń. Klucz budujemy z `returnUrl`, bo przy
 * braku `transaction` nie ma w danych ani `orderId`, ani `transRemoteID`.
 */
const reportedMissingConfirmationFee = new Set()

/**
 * Metoda zgłaszająca dane potwierdzenia bez prowizji.
 *
 * Po dołożeniu strażnika ten stan przestał rzucać, czyli **przestał być widoczny w Sentry sam
 * z siebie** - a oznacza potwierdzenie, na którym płatnik nie widzi opłaty, choć serwis ma włączony
 * model płatnika (`showFrontFees`). Lista kluczy odpowiedzi mówi, czy backend przysłał inny kształt,
 * czy urwaną odpowiedź - bez niej nie da się napisać zgłoszenia.
 *
 * ⚠️ Raport ma sens WYŁĄCZNIE dla stanów z `CONFIRMATION_STATE_WITH_FEE_LIST` i tylko stamtąd jest
 * wołany: na ekranie błędu brak `transaction` jest zachowaniem normalnym, a nie anomalią kontraktu.
 */
function reportMissingConfirmationFee(confirmationData) {
  const key = String(confirmationData?.returnUrl ?? confirmationData?.state ?? null)
  if (reportedMissingConfirmationFee.has(key)) {
    return
  }
  reportedMissingConfirmationFee.add(key)
  reportMessage('Potwierdzenie: dane bez prowizji', {
    level: REPORT_LEVEL.warning,
    tags: { flow: 'payment-fee' },
    extra: {
      state: confirmationData?.state ?? null,
      serviceType: confirmationData?.serviceType ?? null,
      czyTransaction: !!confirmationData?.transaction,
      kluczeOdpowiedzi: confirmationData ? Object.keys(confirmationData) : null,
    },
  })
}

export const isPaywallView = (paymentData) => paymentData !== null

export const isConfirmationView = (confirmationData) => confirmationData !== null

/**
 * Czy stan potwierdzenia dopuszcza pokazanie prowizji.
 *
 * Lista stanów siedzi w `confirmation.const.js` - kryterium i skutki zdjęcia `PENDING` opisuje
 * komentarz przy `CONFIRMATION_STATE_WITH_FEE_LIST`. Tutaj liczy się jedno: `isConfirmationView`
 * to zwykłe `!== null`, więc SAMA obecność danych potwierdzenia nie znaczy jeszcze, że jest co
 * pokazywać - ekrany błędu też je mają.
 */
export const isConfirmationFeeState = (confirmationData) =>
  CONFIRMATION_STATE_WITH_FEE_LIST.includes(confirmationData?.state)

export const isPaymentFeeActive = ({ paymentData = null, confirmationData = null } = {}) => {
  // Dane potwierdzenia wchodzą do magazynu również na ekranach błędu (i w trakcie płatności, przez
  // odpytywanie `getConfirmationTrxSilent`), a `Fee.vue` renderuje wtedy gałąź potwierdzenia -
  // dlatego bramka stanu stoi PRZED wyborem źródła danych, nie w gałęzi `confirmationData`.
  if (isConfirmationView(confirmationData) && !isConfirmationFeeState(confirmationData)) return false

  const data = isPaywallView(paymentData) ? paymentData : confirmationData
  // sprawdzamy flagę - czy wyświetlać prowizję - bez tego mamy model merchanta a nie płatnika
  // weryfikacja SIDa (PAYMENT_FEE_SID_CONFIG) odbywa się wyłącznie w store payment
  if (!data || !data?.visibilityOptions) return false
  return data.visibilityOptions.showFrontFees
}

export const isPaymentFeeLegacyMode = (paymentData = null) => {
  return paymentData != null && paymentData.customerFee !== undefined
}

export const getPaymentFeeList = (paymentData = null) => {
  return (paymentData?.paywayGroups || [])
    .flatMap((group) => group.payways || [])
    .map((payway) => Number(payway.customerFee))
}

export const isPaymentFeeDifferent = ({ paymentData = null, confirmationData = null } = {}) => {
  if (isConfirmationView(confirmationData)) {
    return false
  }

  return new Set(getPaymentFeeList(paymentData)).size > 1
}

export const getPaymentFeeAsSingleValue = ({ paymentData = null, confirmationData = null } = {}) => {
  if (isConfirmationView(confirmationData)) {
    // Stan bez prowizji (ekran błędu) kończy się tutaj - bez raportu, bo brak `transaction` jest
    // tam NORMALNY: przeterminowana transakcja Visa Mobile to wpis `{ state: 'OUTDATED' }`
    // zapisany przez sam front (`VisaMobileProcess/Data.vue`, wyczerpany licznik odpytań), a nie
    // urwana odpowiedź API. Zdjęcie tej gałęzi wraca do raportowania własnego zapisu jako awarii
    // backendu (grupa #4294 w Sentry: 12 zdarzeń, 12 razy ten sam kształt).
    if (!isConfirmationFeeState(confirmationData)) {
      return null
    }
    // `?.` stało tylko przy `confirmationData` i nie chroniło `transaction` - a `isConfirmationView`
    // sprawdza wyłącznie `!== null`, więc każda odpowiedź `/confirmation` bez `transaction` rzucała
    // (`reading 'customerFee'` w Blinku, `t.transaction is undefined` w WebKicie - stąd osobne grupy
    // w Sentry o jednakowej przyczynie). Zwracamy `null`, bo `formatFee(undefined)` dałoby „NaN".
    const customerFee = confirmationData?.transaction?.customerFee
    if (customerFee === undefined || customerFee === null) {
      reportMissingConfirmationFee(confirmationData)
      return null
    }
    return formatFee(customerFee)
  }

  if (isPaymentFeeLegacyMode(paymentData)) {
    return formatFee(paymentData.customerFee)
  }

  if (!isPaymentFeeDifferent({ paymentData, confirmationData })) {
    const feeList = getPaymentFeeList(paymentData)
    return Number(feeList[0]) || 0
  }

  return null
}

/**
 * Metoda podająca prowizję płatnika dla grupy kanałów - jedną stawkę albo zakres.
 *
 * ⚠️ `group` bywa `undefined`, mimo że szablon wygląda na zabezpieczony: `selectedGroup`
 * w `useOrderDetails` szuka grupy przez `.find()` po `selectedPaywayGroupId`, a `OrderDetails/Fee.vue`
 * sprawdza przed wywołaniem tylko `selectedPaywayGroupType`. Kiedy lista grup zmieni się pod
 * wybranym identyfikatorem (odświeżenie paywalla, chowanie grup przez PaymentBoost), `.find()`
 * oddaje `undefined` i `group.payways` rzucało `Cannot read properties of undefined` - grupy
 * #4432/#4433 w Sentry. `Fee.vue` jest przy tym poza zasięgiem `vue/no-undef-properties`
 * (nieliteralny argument `defineProps`), więc lint tej ścieżki nie pilnuje.
 *
 * ⚠️ Brak grupy zwraca `null`, a NIE `formatFee(0)`: „0,00" byłoby zdaniem o pieniądzach, którego
 * nie mamy podstaw powiedzieć - tak samo jak `getPaymentFeeAsSingleValue` przy danych
 * potwierdzenia bez `transaction`. Grupa, która payways ma, ale pustą listę, to co innego -
 * tam „0,00" jest prawdziwe i ta gałąź zostaje bez zmian.
 *
 * @param {object|null|undefined} group
 * @param {string} currency
 * @returns {string|null} sformatowana prowizja albo `null`, gdy grupy nie ma
 */
export const getPaymentFeeForGroup = (group, currency = 'PLN') => {
  const payways = group?.payways
  if (!Array.isArray(payways)) return null

  const feeList = payways.map((payway) => Number(payway.customerFee))
  if (feeList.length === 0) return formatFee(0) + ' ' + currency

  const min = Math.min(...feeList)
  const max = Math.max(...feeList)
  return min === max ? formatFee(min) + ' ' + currency : `${formatFee(min)} - ${formatFee(max)} ${currency}`
}

export const getPaymentFeeForPaywayAsNumber = (payway) => Number(payway.customerFee)

export const getPaymentFeeForActivePaywayAsNumber = ({ paymentData = null, paywayID = null } = {}) => {
  if (!paywayID) return 0

  const payway = (paymentData?.paywayGroups || [])
    .flatMap((group) => group.payways || [])
    .find((payway) => payway.id === paywayID)

  return payway ? Number(payway.customerFee) : 0
}

export const getPaymentFeeForPayway = (payway, currency = 'PLN') => {
  return formatFee(payway.customerFee) + ' ' + currency
}

export const sortPaymentGroups = (groups = []) => {
  const idx = groups.findIndex((group) => group.type === PAYWAY_GROUPS_TYPES.blik)
  if (idx === -1) return [...groups]
  return [...groups.slice(0, idx), ...groups.slice(idx + 1), groups[idx]]
}
